import { Box, Button, Chip, Grid, MenuItem } from '@mui/material';
import Text from 'components/text';
import { ReactNode, useEffect, useState } from 'react';
import api from 'utils/api';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import copyToClipboard from 'utils/copyToClipboard';
import { formatShortDate } from 'utils/helpers';
import { Check, Download, EyeOff, Pencil, X } from 'lucide-react';
import Card from 'components/card';
import Row from 'components/row';
import BaseInput from 'components/inputs/base-input';
import star from 'assets/icons/star.svg';
import { youtubeUrl } from '../../../../utils/youtube';
import { Invitation, InvitationContract } from './invitation.types';

// Values must match the backend's DistributionStatus enum (update-invitation.dto.ts) — others get a 400;
// labels mirror the New Invitation form's select
const DISTRIBUTION_STATUSES = [
  { value: 'redistributed', label: 'Re-distributed to Snafu' },
  { value: 'other', label: 'Other' },
];
const YES_NO = [
  { value: 'YES', label: 'YES' },
  { value: 'NO', label: 'NO' },
];
const SOURCE_LABELS = { automation: 'Automation', manual: 'Manual' };

interface InvitationDraft {
  advanceAmount: string;
  termLength: string;
  distributionStatus: string;
  educationAccess: boolean;
  analyticsAccess: boolean;
}

interface DraftProps {
  draft: InvitationDraft | null;
  setDraft: (draft: InvitationDraft) => void;
}

const kindOf = (invite: Invitation) => (invite.spotifyId || invite.youtubeId ? 'Artist' : 'Label');

const text = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === '' ? '—' : String(value);

const formatFileSize = (bytes: number) =>
  bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

const contractTypeLabel = (contentType: string | null) =>
  contentType?.split('/').pop()?.toUpperCase() ?? null;

const getImage = (images: { url: string }[] | null | undefined) =>
  images && images.length > 0 ? images[0].url : null;

const actionSx = {
  cursor: 'pointer',
  userSelect: 'none',
  opacity: 0.7,
  color: '#111',
  display: 'flex',
  alignItems: 'center',
  '&:hover': { opacity: 1 },
} as const;

const editInputProps = {
  variant: 'filled',
  hiddenLabel: true,
  size: 'small',
  error: false,
} as const;

const EditNumber = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
  <BaseInput
    {...editInputProps}
    type="number"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    sx={{ width: 140 }}
    inputProps={{ style: { textAlign: 'right' } }}
  />
);

const EditSelect = ({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) => (
  <BaseInput
    {...editInputProps}
    select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    sx={{ minWidth: 160 }}
  >
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ))}
  </BaseInput>
);

const YesNoRow = ({
  title,
  value,
  draftValue,
  onChange,
}: {
  title: string;
  value: boolean;
  draftValue: boolean | undefined;
  onChange: (value: boolean) => void;
}) => (
  <Row
    title={title}
    value={
      draftValue === undefined ? (
        value ? (
          'YES'
        ) : (
          'NO'
        )
      ) : (
        <EditSelect
          value={draftValue ? 'YES' : 'NO'}
          options={YES_NO}
          onChange={(v) => onChange(v === 'YES')}
        />
      )
    }
  />
);

const SourceChip = ({ source }: { source: Invitation['source'] }) => (
  <Chip
    size="small"
    label={source === 'automation' ? 'Automated' : 'Manual'}
    sx={{
      height: 24,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: '.02em',
      textTransform: 'uppercase',
      bgcolor: source === 'automation' ? 'rgba(120,1,239,.1)' : '#F1F5F9',
      color: source === 'automation' ? '#6A2BC2' : '#64748B',
    }}
  />
);

/** A row value with a copy-to-clipboard icon after it. */
const Copyable = ({
  value,
  children,
  onCopied,
}: {
  value: string;
  children: ReactNode;
  onCopied: () => void;
}) => (
  <Box display="flex" alignItems="center" gap={1} minWidth={0}>
    <Text fontSize={16.5} color="#444" textAlign="right" overflow="hidden" textOverflow="ellipsis">
      {children}
    </Text>
    <Box
      title="Copy"
      aria-label={`Copy ${value}`}
      onClick={() => {
        copyToClipboard(value);
        onCopied();
      }}
      sx={actionSx}
    >
      <ContentCopyIcon sx={{ fontSize: 15 }} />
    </Box>
  </Box>
);

const ArtistDetailsCard = ({
  invite,
  onToast,
}: {
  invite: Invitation;
  onToast: (message: string) => void;
}) => {
  const copied = (what: string) => () => onToast(`${what} copied to clipboard!`);
  return (
    <Card>
      <Card.Head>Artist Details</Card.Head>
      <Card.Body>
        <Row
          title="Name"
          value={
            <Copyable value={invite.artistName} onCopied={copied('Name')}>
              {invite.artistName}
            </Copyable>
          }
        />
        <Row title="Kind" value={kindOf(invite)} />
        <Row
          title="Email"
          value={
            <Copyable value={invite.artist.email} onCopied={copied('Email')}>
              {invite.artist.email}
            </Copyable>
          }
        />
        {invite.spotifyId && (
          <Row
            title="Spotify"
            value={
              <Copyable value={invite.spotifyId} onCopied={copied('Spotify ID')}>
                <a
                  target="_blank"
                  href={`https://open.spotify.com/artist/${invite.spotifyId}`}
                  rel="noreferrer"
                >
                  {invite.spotifyId}
                </a>
              </Copyable>
            }
          />
        )}
        {invite.youtubeId && (
          <Row
            title="Youtube"
            value={
              <Copyable value={invite.youtubeId} onCopied={copied('YouTube ID')}>
                {youtubeUrl(invite.youtubeId) !== null ? (
                  <a target="_blank" href={youtubeUrl(invite.youtubeId) || ''} rel="noreferrer">
                    {invite.youtubeId}
                  </a>
                ) : (
                  invite.youtubeId
                )}
              </Copyable>
            }
          />
        )}
      </Card.Body>
    </Card>
  );
};

const InvitationDetailsCard = ({
  invite,
  draft,
  setDraft,
}: { invite: Invitation } & DraftProps) => (
  <Card>
    <Card.Head>Invitation Details</Card.Head>
    <Card.Body>
      <Row title="Status" value={invite.status.toUpperCase()} />
      <Row title="Source" value={SOURCE_LABELS[invite.source]} />
      <Row title="Invited By" value={text(invite.inviter?.fullName)} />
      <Row title="Created" value={formatShortDate(invite.createdAt)} />
      <Row title="Reminded" value={formatShortDate(invite.remindedAt)} />
      <Row title="Escalated" value={formatShortDate(invite.escalatedAt)} />
      <Row title="Accepted" value={formatShortDate(invite.acceptedAt)} />
      <Row title="Expires" value={invite.expiresAt ? formatShortDate(invite.expiresAt) : 'Never'} />
      <YesNoRow
        title="Education"
        value={invite.educationAccess}
        draftValue={draft?.educationAccess}
        onChange={(educationAccess) => draft && setDraft({ ...draft, educationAccess })}
      />
      <YesNoRow
        title="Analytics"
        value={invite.analyticsAccess}
        draftValue={draft?.analyticsAccess}
        onChange={(analyticsAccess) => draft && setDraft({ ...draft, analyticsAccess })}
      />
    </Card.Body>
  </Card>
);

const ContractLine = ({
  contract,
  onView,
}: {
  contract: InvitationContract;
  onView: () => void;
}) => {
  const meta = [
    contractTypeLabel(contract.contentType),
    contract.size !== null && formatFileSize(contract.size),
    `Added ${formatShortDate(contract.createdAt)}`,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} padding={1}>
      <Box minWidth={0}>
        <Text
          fontSize={16}
          title={contract.fileName}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {contract.fileName}
        </Text>
        <Text fontSize={14} color="#64748B" marginTop={0.5}>
          {meta}
        </Text>
      </Box>
      <Button
        variant="outlined"
        color="inherit"
        size="small"
        onClick={onView}
        startIcon={<Download size={16} />}
        sx={{ flexShrink: 0, textTransform: 'none', borderColor: '#D5DCE5' }}
      >
        View
      </Button>
    </Box>
  );
};

const ContractList = ({
  contracts,
  onView,
}: {
  contracts: InvitationContract[];
  onView: (documentId: number) => void;
}) =>
  contracts.length === 0 ? (
    <Text fontSize={15} color="#64748B" padding={1}>
      No contract
    </Text>
  ) : (
    <>
      {contracts.map((contract) => (
        <ContractLine key={contract.id} contract={contract} onView={() => onView(contract.id)} />
      ))}
    </>
  );

const DealContractCard = ({
  invite,
  draft,
  setDraft,
  distributionOptions,
  onViewContract,
  onToast,
}: {
  invite: Invitation;
  distributionOptions: { value: string; label: string }[];
  onViewContract: (documentId: number) => void;
  onToast: (message: string) => void;
} & DraftProps) => {
  const copied = (what: string) => () => onToast(`${what} copied to clipboard!`);
  return (
    <Card>
      <Card.Head>Deal &amp; Contract</Card.Head>
      <Card.Body>
        {!invite.deal ? (
          <Text fontSize={16} color="#64748B" padding={1}>
            No deal — artist not signed yet.
          </Text>
        ) : (
          <>
            <Row
              title="Deal ID"
              value={
                <Copyable value={String(invite.deal.id)} onCopied={copied('Deal ID')}>
                  {invite.deal.id}
                </Copyable>
              }
            />
            <Row
              title="HubSpot Deal ID"
              value={
                invite.deal.hubspotDealId ? (
                  <Copyable value={invite.deal.hubspotDealId} onCopied={copied('HubSpot Deal ID')}>
                    {invite.deal.hubspotDealId}
                  </Copyable>
                ) : (
                  '—'
                )
              }
            />
            <Row
              title="Advance Amount"
              value={
                draft ? (
                  <EditNumber
                    value={draft.advanceAmount}
                    onChange={(advanceAmount) => setDraft({ ...draft, advanceAmount })}
                  />
                ) : (
                  text(invite.advanceAmount)
                )
              }
            />
            <Row
              title="Term Length"
              value={
                draft ? (
                  <EditNumber
                    value={draft.termLength}
                    onChange={(termLength) => setDraft({ ...draft, termLength })}
                  />
                ) : (
                  text(invite.termLength)
                )
              }
            />
            <Row
              title="Distribution Status"
              value={
                draft ? (
                  <EditSelect
                    value={draft.distributionStatus}
                    options={distributionOptions}
                    onChange={(distributionStatus) => setDraft({ ...draft, distributionStatus })}
                  />
                ) : (
                  text(invite.distributionStatus?.toUpperCase())
                )
              }
            />
            <Row title="Deal Status" value={text(invite.deal.dealStatus)} />
            <Row title="First Advance" value={text(invite.deal.firstAdvance)} />
            <Row title="First Advance Status" value={text(invite.deal.firstAdvanceStatus)} />
            <Row title="Paid On" value={formatShortDate(invite.deal.firstAdvancePaidAt)} />
            <Row title="Last Synced" value={formatShortDate(invite.deal.customerFileSyncedAt)} />
            <Box borderTop={1} borderColor="#ddd" marginTop={1} paddingTop={1}>
              <ContractList contracts={invite.deal.contracts} onView={onViewContract} />
            </Box>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default function InvitedRow({
  invite,
  open,
  onToggle,
  canEdit,
  onSaved,
  onToast,
}: {
  invite: Invitation;
  open: boolean;
  onToggle: () => void;
  canEdit: boolean;
  onSaved: (id: number, patch: Partial<Invitation>) => void;
  onToast: (message: string) => void;
}) {
  const [draft, setDraft] = useState<InvitationDraft | null>(null);
  const [saving, setSaving] = useState(false);

  const editing = draft !== null;

  // Expanding another invitation collapses this row — discard any in-progress edit with it
  useEffect(() => {
    if (!open) setDraft(null);
  }, [open]);

  const isValidDistributionStatus =
    !!draft && DISTRIBUTION_STATUSES.some((option) => option.value === draft.distributionStatus);

  const distributionOptions =
    !draft || isValidDistributionStatus
      ? DISTRIBUTION_STATUSES
      : [
          {
            value: draft.distributionStatus,
            label: (draft.distributionStatus ?? '').toUpperCase(),
          },
          ...DISTRIBUTION_STATUSES,
        ];

  const startEdit = () => {
    setDraft({
      advanceAmount: invite.advanceAmount?.toString() ?? '',
      termLength: invite.termLength?.toString() ?? '',
      distributionStatus: invite.distributionStatus ?? 'other',
      educationAccess: invite.educationAccess,
      analyticsAccess: invite.analyticsAccess,
    });
    if (!open) onToggle();
  };

  const cancelEdit = () => setDraft(null);

  const saveEdit = () => {
    if (!draft || saving) return;

    const patch: Partial<Invitation> = {
      advanceAmount: Number(draft.advanceAmount) || 0,
      termLength: Number(draft.termLength) || 0,
      educationAccess: draft.educationAccess,
      analyticsAccess: draft.analyticsAccess,
    };
    // Legacy values outside the enum are shown but can't be submitted — omit to keep them unchanged
    if (isValidDistributionStatus) {
      patch.distributionStatus = draft.distributionStatus;
    }

    setSaving(true);
    api.invitation
      .updateInvitation({ id: invite.id, data: patch })
      .then(() => {
        onSaved(invite.id, patch);
        setDraft(null);
        onToast('Invitation updated');
      })
      .catch((err: { message?: string }) => {
        onToast(typeof err?.message === 'string' ? err.message : 'Failed to update invitation');
      })
      .finally(() => setSaving(false));
  };

  // The tab is opened synchronously on the click so popup blockers let it through
  const openContract = (documentId: number) => {
    const tab = window.open('', '_blank');
    api.invitation
      .getContractUrl({ id: invite.id, documentId })
      .then((res) => {
        const { downloadUrl } = res as { downloadUrl: string };
        if (tab) tab.location.href = downloadUrl;
        else window.open(downloadUrl, '_blank');
      })
      .catch(() => {
        tab?.close();
        onToast('Failed to open contract');
      });
  };

  return (
    <Box borderBottom={1} borderColor="#ddd" paddingBottom={2}>
      <Box display="flex" gap={2}>
        <Box border={0}>
          <IconButton aria-label="expand row" size="small" disabled={editing} onClick={onToggle}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>
        <Box>
          <img
            src={getImage(invite.deal?.images) || star}
            alt="artist"
            width="40"
            height="40"
            style={{ borderRadius: '22px', minWidth: '40px' }}
          />
        </Box>
        <Box flexGrow={1}>
          <Box
            marginBottom={0.6}
            display="flex"
            gap={2}
            minHeight={40}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
              <Box
                width={20}
                height={20}
                bgcolor={invite.status === 'accepted' ? '#12a621' : '#555'}
                borderRadius={10}
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="#fff"
              >
                {
                  {
                    accepted: <Check size={16} />,
                    pending: <EyeOff size={16} />,
                  }[invite.status]
                }
              </Box>
              <Text fontSize={18}>{invite.id}</Text>
              <Text fontSize={18}>{invite.artistName}</Text>
              <Text fontSize={16} color="#64748B">
                {kindOf(invite)}
              </Text>
              <SourceChip source={invite.source} />
            </Box>
            <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
              <Text
                fontSize={14}
                color="#64748B"
                display={{ xs: 'none', md: 'block' }}
                sx={{ '& b': { fontWeight: 500, color: '#111' } }}
              >
                Created <b>{formatShortDate(invite.createdAt)}</b> · Expires{' '}
                <b>{invite.expiresAt ? formatShortDate(invite.expiresAt) : 'Never'}</b>
              </Text>
              {editing ? (
                <>
                  <Box
                    title="Save changes"
                    onClick={saveEdit}
                    sx={{ ...actionSx, '&:hover': { opacity: 1, color: '#12a621' } }}
                  >
                    <Check size={18} />
                  </Box>
                  <Box title="Discard changes" onClick={cancelEdit} sx={actionSx}>
                    <X size={18} />
                  </Box>
                </>
              ) : (
                <>
                  {canEdit && (
                    <Box title="Edit details" onClick={startEdit} sx={actionSx}>
                      <Pencil size={16} />
                    </Box>
                  )}
                  <Box
                    flexDirection="row"
                    gap={0.5}
                    onClick={() => {
                      onToast('Magic link copied to clipboard!');
                      copyToClipboard(invite.magicLink);
                    }}
                    sx={actionSx}
                  >
                    <ContentCopyIcon sx={{ fontSize: 16, color: '#111' }} />
                    <Text fontSize={16} color="#000" display={{ xs: 'none', sm: 'block' }}>
                      Copy Magic Link
                    </Text>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Grid container spacing={2} paddingY={2} paddingLeft={{ xs: 0, lg: 6 }}>
          <Grid item xs={12} md={6} lg={4}>
            <ArtistDetailsCard invite={invite} onToast={onToast} />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <InvitationDetailsCard invite={invite} draft={draft} setDraft={setDraft} />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <DealContractCard
              invite={invite}
              draft={draft}
              setDraft={setDraft}
              distributionOptions={distributionOptions}
              onViewContract={openContract}
              onToast={onToast}
            />
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );
}
